import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TooltipProvider } from '@/components/ui/tooltip';
import { 
  Clock, 
  CheckSquare, 
  TrendingUp, 
  Users
} from 'lucide-react';
import { TimesheetStats } from '@/components/TimesheetStats';
import { PersonalInsightsCard } from '@/components/AIAssistant/PersonalInsightsCard';
import { RejectionCounterWidget } from '@/components/RejectionCounterWidget';
import { PersonaBadgeToggle } from '@/components/dashboard/PersonaBadgeToggle';
import { ThisWeekCard } from '@/components/dashboard/ThisWeekCard';
import { ActionRequiredCard } from '@/components/dashboard/ActionRequiredCard';
import { TopProjectsCard } from '@/components/dashboard/TopProjectsCard';
import { QuickSummaryCard } from '@/components/dashboard/QuickSummaryCard';
import { SubmissionConsistencyCard } from '@/components/dashboard/SubmissionConsistencyCard';
import { QuickEntryModalEnhanced } from '@/components/AIAssistant/QuickEntryModalEnhanced';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { aiService } from '@/services/aiService';
import type { TimesheetEntry } from '@/types/timesheet';
import { toast } from 'sonner';
import { getViolationsForEmployee, getAllViolations } from '@/data/mockPolicyViolations';
import { AlertTriangle } from 'lucide-react';

interface DashboardPageProps {
  onNavigate?: (path: string) => void;
}

// Main Dashboard Component
export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
  const { currentEmployee, timesheets, projects, updateTimesheet } = useTimesheetForScreen('dashboard');
  // Get pending approvals from the approvals screen to ensure consistency
  const { pendingApprovals: approvalsPending, employees } = useTimesheetForScreen('approvals');
  const { getScreenMode } = useDataSource();
  const { summary } = useDashboardSummary(currentEmployee.id, 'dashboard');
  const [showQuickEntryModal, setShowQuickEntryModal] = useState(false);
  
  const mode = getScreenMode('dashboard');

  // Generate AI insights
  const personalInsights = useMemo(() => {
    return aiService.generatePersonalInsights(currentEmployee.id, timesheets);
  }, [currentEmployee.id, timesheets]);

  // Helper function to get relative time
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    return format(date, 'dd/MM/yyyy');
  };

  // Generate recent activity from timesheets
  const recentActivity = useMemo(() => {
    const activities: Array<{
      action: string;
      description: string;
      time: string;
      icon: any;
      color: string;
      timestamp: Date;
    }> = [];

    // Get timesheets for current employee
    const myTimesheets = timesheets
      .filter(ts => ts.employeeId === currentEmployee.id)
      .sort((a, b) => {
        const dateA = a.approvedAt || a.submittedAt || new Date(a.weekEnding);
        const dateB = b.approvedAt || b.submittedAt || new Date(b.weekEnding);
        return dateB.getTime() - dateA.getTime();
      });

    // Add timesheet activities
    myTimesheets.slice(0, 5).forEach(ts => {
      if (ts.status === 'approved' && ts.approvedAt) {
        activities.push({
          action: 'Timesheet Approved',
          description: `Week ending ${format(new Date(ts.weekEnding), 'dd/MM/yyyy')}`,
          time: getRelativeTime(ts.approvedAt),
          icon: CheckSquare,
          color: 'text-green-600',
          timestamp: ts.approvedAt
        });
      } else if (ts.status === 'submitted' && ts.submittedAt) {
        activities.push({
          action: 'Timesheet Submitted',
          description: `Week ending ${format(new Date(ts.weekEnding), 'dd/MM/yyyy')}`,
          time: getRelativeTime(ts.submittedAt),
          icon: Clock,
          color: 'text-blue-600',
          timestamp: ts.submittedAt
        });
      } else if (ts.status === 'rejected' && ts.rejectedAt) {
        activities.push({
          action: 'Timesheet Rejected',
          description: `Week ending ${format(new Date(ts.weekEnding), 'dd/MM/yyyy')}`,
          time: getRelativeTime(ts.rejectedAt),
          icon: Clock,
          color: 'text-red-600',
          timestamp: ts.rejectedAt
        });
      }
    });

    // Sort by timestamp and take most recent 3
    return activities
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 3);
  }, [currentEmployee.id, timesheets]);

  // Calculate user-specific metrics
  const userMetrics = useMemo(() => {
    // Calculate pending approvals from approvals screen data (uses managerId-based filtering)
    const userPendingApprovals = approvalsPending.length;
    const overdueApprovals = approvalsPending.filter(ts => {
      if (!ts.submittedAt) return false;
      const daysSinceSubmission = Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24));
      return daysSinceSubmission > 3;
    }).length;

    // Use live data from backend if available and in live mode
    if (mode === 'live' && summary?.performanceMetrics?.utilization) {
      return {
        pendingApprovals: userPendingApprovals,
        overdueApprovals,
        utilizationRate: Math.round(summary.performanceMetrics.utilization.rate),
        utilizationTrend: Math.round(summary.performanceMetrics.utilization.trend),
      };
    }

    // Fall back to demo data calculation
    // Filter timesheets for current user only
    const userTimesheets = timesheets.filter(ts => ts.employeeId === currentEmployee.id);

    // Calculate user's personal utilization
    const approvedTimesheets = userTimesheets.filter(ts => ts.status === 'approved');
    
    const totalHours = approvedTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
      }, 0);
    }, 0);

    const billableHours = approvedTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        const project = projects.find(p => p.id === entry.projectId);
        const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
        return entrySum + (project?.billable ? hours : 0);
      }, 0);
    }, 0);

    const utilizationRate = totalHours > 0 ? (billableHours / totalHours) * 100 : 0;

    // Calculate utilization trend (compare with previous period)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    const recentTimesheets = approvedTimesheets.filter(ts => 
      ts.weekEnding >= thirtyDaysAgo && ts.weekEnding <= now
    );
    const previousTimesheets = approvedTimesheets.filter(ts => 
      ts.weekEnding >= sixtyDaysAgo && ts.weekEnding < thirtyDaysAgo
    );

    const recentTotalHours = recentTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
      }, 0);
    }, 0);

    const recentBillableHours = recentTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        const project = projects.find(p => p.id === entry.projectId);
        const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
        return entrySum + (project?.billable ? hours : 0);
      }, 0);
    }, 0);

    const previousTotalHours = previousTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
      }, 0);
    }, 0);

    const previousBillableHours = previousTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        const project = projects.find(p => p.id === entry.projectId);
        const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
        return entrySum + (project?.billable ? hours : 0);
      }, 0);
    }, 0);

    const recentUtilization = recentTotalHours > 0 ? (recentBillableHours / recentTotalHours) * 100 : 0;
    const previousUtilization = previousTotalHours > 0 ? (previousBillableHours / previousTotalHours) * 100 : 0;
    const utilizationTrend = recentUtilization - previousUtilization;

    return {
      pendingApprovals: userPendingApprovals,
      overdueApprovals,
      utilizationRate: Math.round(utilizationRate),
      utilizationTrend: Math.round(utilizationTrend),
    };
  }, [mode, summary, currentEmployee.id, timesheets, projects, approvalsPending]);

  // Handle quick entry submission
  const handleQuickEntryAdded = (entry: Omit<TimesheetEntry, 'id'>) => {
    // Find or create current week timesheet
    const currentWeekEnd = new Date();
    currentWeekEnd.setDate(currentWeekEnd.getDate() + (7 - currentWeekEnd.getDay()));
    
    const existingTimesheet = timesheets.find(
      ts => ts.employeeId === currentEmployee.id &&
      ts.weekEnding.toDateString() === currentWeekEnd.toDateString()
    );

    if (existingTimesheet) {
      // Add entry to existing timesheet
      const newEntry = {
        ...entry,
        id: `entry-${Date.now()}`
      };
      
      updateTimesheet({
        ...existingTimesheet,
        entries: [...existingTimesheet.entries, newEntry]
      });
      
      toast.success('Time entry added successfully');
    } else {
      toast.error('No timesheet found for current week');
    }
    
    setShowQuickEntryModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header with integrated persona toggle */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Overview of your timesheet activity and key metrics
          </p>
        </div>
        <div className="flex-shrink-0">
          <PersonaBadgeToggle />
        </div>
      </div>

      {/* ========================================
          TIER 1: HERO SECTION - Most Important
          ======================================== */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* This Week Card - Primary Focus */}
        <ThisWeekCard 
          onNavigateToTimesheet={() => onNavigate?.('/timesheet')}
          onQuickEntry={() => setShowQuickEntryModal(true)}
        />
        
        {/* Action Required Card - Critical Alerts */}
        <ActionRequiredCard 
          onNavigateToTimesheet={(timesheetId, weekEnding) => {
            if (weekEnding) {
              const weekParam = format(weekEnding, 'yyyy-MM-dd');
              onNavigate?.(`/timesheet?week=${weekParam}`);
            } else {
              onNavigate?.('/timesheet');
            }
          }}
          onViewAllActions={() => onNavigate?.('/timesheet')}
        />

        {/* PHASE 2: Quick Summary Card - At-a-glance Status */}
        <QuickSummaryCard onNavigate={onNavigate} />
      </div>

      {/* ========================================
          TIER 2: PERFORMANCE METRICS - Key Numbers
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Performance Metrics
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        
        <TooltipProvider>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Existing TimesheetStats component - Billable, This Month, Avg Weekly */}
            <TimesheetStats onNavigate={onNavigate} />
          
            {/* My Utilization Card - Enhanced with visual accents */}
            <Card className={`hover:shadow-lg transition-all duration-200 ${
              userMetrics.utilizationRate >= 85 ? 'border-l-4 border-l-accent-success-500 bg-gradient-to-br from-accent-success-50/50 to-transparent dark:from-accent-success-900/20' :
              userMetrics.utilizationRate >= 75 ? 'border-l-4 border-l-accent-info-500 bg-gradient-to-br from-accent-info-50/50 to-transparent dark:from-accent-info-900/20' :
              userMetrics.utilizationRate >= 60 ? 'border-l-4 border-l-accent-warning-500 bg-gradient-to-br from-accent-warning-50/50 to-transparent dark:from-accent-warning-900/20' :
              'border-l-4 border-l-accent-destructive-500 bg-gradient-to-br from-accent-destructive-50/50 to-transparent dark:from-accent-destructive-900/20'
            }`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1.5 pt-3 px-3">
                <CardTitle className="text-sm font-semibold">My Utilization</CardTitle>
                <TrendingUp className={`h-5 w-5 ${
                  userMetrics.utilizationRate >= 85 ? 'text-accent-success-500' :
                  userMetrics.utilizationRate >= 75 ? 'text-accent-info-500' :
                  userMetrics.utilizationRate >= 60 ? 'text-accent-warning-500' :
                  'text-accent-destructive-500'
                }`} />
              </CardHeader>
              <CardContent className="px-3 pb-3 pt-1">
                <div className={`text-3xl font-bold ${
                  userMetrics.utilizationRate >= 85 ? 'text-accent-success-600 dark:text-accent-success-400' :
                  userMetrics.utilizationRate >= 75 ? 'text-accent-info-600 dark:text-accent-info-400' :
                  userMetrics.utilizationRate >= 60 ? 'text-accent-warning-600 dark:text-accent-warning-400' :
                  'text-accent-destructive-600 dark:text-accent-destructive-400'
                }`}>
                  {userMetrics.utilizationRate}%
                </div>
                <p className={`text-xs font-medium mt-1 ${
                  userMetrics.utilizationTrend > 0 ? 'text-accent-success-600 dark:text-accent-success-400' : 
                  userMetrics.utilizationTrend < 0 ? 'text-accent-warning-600 dark:text-accent-warning-400' : 
                  'text-muted-foreground'
                }`}>
                  {userMetrics.utilizationTrend !== 0 ? (
                    <>
                      {userMetrics.utilizationTrend > 0 ? '↗' : '↘'} {userMetrics.utilizationTrend > 0 ? '+' : ''}{userMetrics.utilizationTrend}% from last month
                    </>
                  ) : (
                    <>→ No change from last month</>
                  )}
                </p>
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>

        {/* PHASE 2: Submission Consistency Card - Full width below metrics */}
        <SubmissionConsistencyCard />
      </div>

      {/* ========================================
          TIER 3: TIME ALLOCATION - Projects & Distribution
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Time Allocation
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        
        {/* Top Projects by Time Card */}
        <TopProjectsCard 
          onNavigateToProject={(projectId) => {
            console.log('Navigate to project:', projectId);
            onNavigate?.('/timesheet');
          }}
          maxProjects={5}
          defaultTimePeriod="last30days"
          showActiveOnly={false}
        />
      </div>

      {/* ========================================
          TIER 4: INSIGHTS & TRENDS - AI & Analytics
          ======================================== */}
      {personalInsights.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-border" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Insights & Trends
            </h2>
            <div className="h-px flex-1 bg-border" />
          </div>
          
          {/* AI Personal Insights */}
          <PersonalInsightsCard insights={personalInsights} />
        </div>
      )}

      {/* ========================================
          TIER 5: ACTIVITY & SECONDARY ACTIONS
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Activity & Actions
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Activity</CardTitle>
              <CardDescription>Your latest timesheet submissions and approvals</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.length > 0 ? (
                  recentActivity.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-lg bg-accent ${item.color}`}>
                        <item.icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{item.action}</p>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                      <p className="text-xs text-muted-foreground whitespace-nowrap">{item.time}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No recent activity to display
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Secondary Actions */}
          <div className="space-y-4">
            {/* Pending Approvals Card */}
            <Card 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onNavigate?.('/approvals')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
                <CheckSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userMetrics.pendingApprovals}</div>
                <p className="text-xs text-muted-foreground">
                  {userMetrics.overdueApprovals > 0 ? (
                    <span className="text-warning-600 dark:text-warning-400">
                      {userMetrics.overdueApprovals} overdue
                    </span>
                  ) : (
                    <span className="text-success-600 dark:text-success-400">
                      All up to date
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>

            {/* Rejection Counter Widget */}
            <RejectionCounterWidget onClick={() => onNavigate?.('/timesheet')} />
          </div>
        </div>
      </div>

      {/* Quick Entry Modal */}
      <QuickEntryModalEnhanced
        open={showQuickEntryModal}
        onOpenChange={setShowQuickEntryModal}
        projects={projects}
        timesheets={timesheets}
        currentEmployeeId={currentEmployee.id}
        onEntryAdded={handleQuickEntryAdded}
      />
    </div>
  );
};
